import TransactionHistoryDelegate from '../delegates/TransactionHistory.delegate';
import { DEFAULT_PAGE_SIZE } from '../treasury.constants';
import { formatPagination } from '../../../utils/pagination';
import { calculateDateRange } from '../../../utils/dates';

/**
 * Owns the paginated transactions list for the active GL tab.
 *
 * Plain JS class — NOT a React component. Same pattern as the GL Account
 * sub-controller: constructor receives the host `page` (the React
 * orchestrator) and every mutation routes through `setState(partial)`,
 * which calls `page.forceUpdate()`.
 *
 * Pagination state is tracked as a single `pageInfo` object —
 * `{ current, perPage, total }` — matching the shape the
 * `@clockout-dev/design-elements` Table consumes via `pageInfo`. The
 * data layer returns the support-ticket contract
 * (`{ items, pageinfo: { page, perPage, hasNext, hasPrev, count } }`),
 * which we map through the shared `formatPagination` util. `fetchPage`
 * REPLACES `rows` — there is no append / hasMore.
 */
class TransactionHistoryController extends TransactionHistoryDelegate {
    constructor(page) {
        super();
        this.page = page;
        this.state = {
            rows: [],
            pageInfo: {
                current: 1,
                perPage: DEFAULT_PAGE_SIZE,
                total: 0,
            },
            loading: false,
            glType: null,
            timeFrame: null,
            dateRange: null,
        };
    }

    setState(partial) {
        const next = typeof partial === 'function' ? partial(this.state) : partial;
        if (!next) return;
        this.state = { ...this.state, ...next };
        if (this.page && typeof this.page.forceUpdate === 'function') {
            this.page.forceUpdate();
        }
    }

    /**
     * Refreshes the list for a given GL tab. Defaults to page 1 (a tab
     * switch must not leave the user on a page that no longer exists
     * for the new dataset), but accepts `initialPage` / `initialPerPage`
     * so the orchestrator can restore the slice from the URL on the
     * first mount.
     *
     * @param {Object} args
     * @param {string} args.glType
     * @param {string|null} [args.timeFrame]
     * @param {Array|null} [args.dateRange]
     * @param {number|null} [args.initialPage] page to restore from the URL
     * @param {number|null} [args.initialPerPage] page size to restore from the URL
     */
    async refresh({ glType, timeFrame = null, dateRange = null, initialPage = null, initialPerPage = null }) {
        this.setState({ glType, timeFrame, dateRange });
        const perPage = initialPerPage ?? this.state.pageInfo.perPage;
        const page = initialPage ?? 1;
        await this.fetchPage(page, perPage);
    }

    /**
     * Replaces `rows` and `pageInfo` with the requested page. Catches
     * any error from the data layer so a transient failure does not
     * leave `loading` stuck on `true`.
     */
    async fetchPage(page, perPage) {
        const nextPerPage = perPage ?? this.state.pageInfo.perPage ?? DEFAULT_PAGE_SIZE;
        const { glType, timeFrame, dateRange } = this.state;
        const { start, end } = calculateDateRange(timeFrame, dateRange);
        this.setLoading(true);
        try {
            const result = await this.page.data.fetchTransactionHistory({
                glType,
                page,
                pageSize: nextPerPage,
                start,
                end,
            });
            const rows = Array.isArray(result?.items) ? result.items : [];
            // Reuse the shared support-ticket pagination util so the
            // `{ page, perPage, hasNext, hasPrev, count }` contract maps to
            // the package Table's `{ current, perPage, total }` shape.
            const pagination = formatPagination(result?.pageinfo);
            const nextPageInfo = pagination
                ? {
                    current: pagination.current,
                    perPage: pagination.pageSize,
                    total: pagination.total,
                }
                : {
                    current: page,
                    perPage: nextPerPage,
                    total: rows.length,
                };
            this.setState({ rows, pageInfo: nextPageInfo });
        } catch (error) {
            console.error('[TransactionHistory] fetchPage failed', error);
            this.setRows([]);
            this.setPageInfo({ current: 1, perPage: nextPerPage, total: 0 });
        } finally {
            this.setLoading(false);
        }
    }

    /**
     * `onChange` handler the package Table calls with
     * `(antdPagination, filters, sorter, extra)`. Only paginate /
     * page-size-change actions trigger a refetch.
     */
    handleTableChange(antdPagination = {}, _filters, _sorter, extra) {
        if (extra?.action && extra.action !== 'paginate') return;
        const nextPage = antdPagination.current ?? 1;
        const nextPerPage = antdPagination.pageSize ?? this.state.pageInfo.perPage;
        this.fetchPage(nextPage, nextPerPage);
    }

    /**
     * Returns props ready to drop into the dumb
     * `TransactionHistorySection` view.
     */
    getViewProps() {
        const { rows, pageInfo, loading } = this.state;
        return {
            rows,
            loading,
            pageInfo,
            onChange: this.handleTableChange.bind(this),
        };
    }
}

export default TransactionHistoryController;
